import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  importProvidersFrom,
  ErrorHandler,
  provideAppInitializer,
  inject
} from "@angular/core";
import { provideRouter, withRouterConfig } from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { provideServiceWorker } from "@angular/service-worker";

import { take } from "rxjs";
import { NzIconModule } from "ng-zorro-antd/icon";
import { provideDesignSystemIcons } from "@frontends-v3/design-system";
import { ApmErrorHandler, ApmModule, ApmService } from "@elastic/apm-rum-angular";
import {
  customHttpInterceptor,
  HelpCenterStateService,
  impersonationHttpInterceptor,
  LektoAuthService,
  OFFLINE_MODE_ACTIVATOR,
  OfflineModeService,
  offlineHttpInterceptor,
  provideAccessibilityWidget,
  provideAssistantTransport,
  provideCustomSelectDataSource,
  provideWhiteLabelTheme
} from "@frontends-v3/shared-core";
import { NzModalModule } from "ng-zorro-antd/modal";

import { environment } from "../environments/environment";

import { appRoutes } from "./app.routes";
import { PLATFORM_ROUTER_CONFIG } from "./core/constants/router-config.constants";
import { AppUpdateService } from "./core/services/app-update.service";
import { OfflineModeActivatorService } from "./features/offline/offline-mode-activator.service";

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // DEV-980: Voltar recusado pelo canDeactivate (runner da Avaliação) não pode reescrever o
    // histórico — ver PLATFORM_ROUTER_CONFIG.
    provideRouter(appRoutes, withRouterConfig(PLATFORM_ROUTER_CONFIG)),
    provideHttpClient(
      // DEV-966: o interceptor offline fica ANTES do de auth — quando responde do snapshot ou
      // enfileira, a requisição nem chega a precisar de token; quando repassa, o auth segue igual.
      withInterceptors([
        impersonationHttpInterceptor,
        offlineHttpInterceptor,
        customHttpInterceptor
      ]),
      withInterceptorsFromDi()
    ),
    { provide: OFFLINE_MODE_ACTIVATOR, useExisting: OfflineModeActivatorService },
    provideWhiteLabelTheme({ appId: "platform" }),
    provideAssistantTransport(
      `${environment.serviceUrl}/api/core/chat`,
      `${environment.serviceUrl}/api/content`
    ),
    provideAccessibilityWidget({ accountId: environment.userwayAccountId }),
    // DEV-1020: shell do app disponível sem rede, para retomar uma atribuição salva no dispositivo.
    // Fora do build `local` (nx serve / docker local), que não gera o ngsw-worker.js.
    provideServiceWorker("ngsw-worker.js", {
      enabled: environment.environmentName !== "local",
      registrationStrategy: "registerWhenStable:30000"
    }),
    provideAppInitializer(() => inject(AppUpdateService).init()),
    provideAppInitializer(() => {
      const authService = inject(LektoAuthService);
      const helpCenterState = inject(HelpCenterStateService);
      const offlineMode = inject(OfflineModeService);
      if (authService.isLoggedIn()) {
        helpCenterState.load().pipe(take(1)).subscribe();
        // DEV-966: reconcilia o Modo Offline com o servidor e drena a fila pendente ao reabrir o app.
        offlineMode.init().pipe(take(1)).subscribe();
      }
    }),
    importProvidersFrom(NzIconModule),
    importProvidersFrom(NzModalModule),
    provideDesignSystemIcons(),
    provideCustomSelectDataSource(),
    provideAnimations(),
    importProvidersFrom(ApmModule),
    ApmService,
    { provide: ErrorHandler, useClass: ApmErrorHandler }
  ]
};
